/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { format } from 'date-fns';
/**
 * Internal Imports
 */
import RoundsReportTable from '../../../components/customTables/reports/patient/roundsReportTable';
import SearchHeader from '../../../components/searchHeader';

import { RootState } from '../../../redux/store';
import {
  clearRoundsReportFilter,
  getPatientRoundsReport,
  getPatientRoundsReportDownload
} from '../../../redux/actions';

import { date } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Filter,
  PatientRoundsReport,
  IUnitWithDisplayName
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ReportContainer } from '../styles/ReportsViews.styles';

interface RoundsReportProps {
  patientRoundsReport: PatientRoundsReport[];
  reportSearchQuery: string;
  searchTerm: string;
  authorizedUser: AuthUserProps;
  selectedUnits: IUnitWithDisplayName[];
  roundsReportFilter: Filter;
  currentPatientRoundsReportPage: number;
  sortKey: string;
  sortDir: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const RoundsReport: FC<RoundsReportProps> = ({
  patientRoundsReport,
  reportSearchQuery,
  searchTerm,
  authorizedUser,
  selectedUnits,
  roundsReportFilter,
  currentPatientRoundsReportPage,
  sortKey,
  sortDir,
  setSearchTerm
}) => {
  const dispatch = useDispatch();
  const today = new Date();
  const dateRange = {
    firstDayOfMonth: format(
      new Date(today.getFullYear(), today.getMonth(), 1),
      'yyyy-MM-dd'
    ),
    todayDate: format(
      new Date(today.getFullYear(), today.getMonth(), today.getDate()),
      'yyyy-MM-dd'
    )
  };

  useEffect(() => {
    return () => {
      dispatch(clearRoundsReportFilter());
    };
  }, []);

  useEffect(() => {
    // fetch data from month to date by default
    dispatch(
      getPatientRoundsReport({
        facilityId: authorizedUser?.facilityId,
        unitId: selectedUnits.map(item => item.id),
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.roundedBy || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.todayDate
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.todayDate
        ),
        page: currentPatientRoundsReportPage
      })
    );
  }, [
    authorizedUser,
    reportSearchQuery,
    roundsReportFilter,
    selectedUnits,
    sortKey,
    sortDir,
    currentPatientRoundsReportPage
  ]);

  const listToDisplay = useMemo(() => {
    if (patientRoundsReport?.length) {
      const roundCleanData =
        selectedUnits?.length > 0
          ? patientRoundsReport?.reduce(
              (
                cleanData: PatientRoundsReport[],
                round: PatientRoundsReport
              ) => {
                // Note: there must be a standardization for detecting the 'unable to round' records.
                // The below logic will cover the existing data.
                return [
                  ...cleanData,
                  {
                    roundId: round?.roundId || '',
                    unit: round?.unit || '',
                    unitId: round?.unitId || '',
                    room: round?.room || '',
                    patientId: round?.patientId || '',
                    patient: `${round?.patientFirstName} ${round?.patientLastName}`,
                    patientFirstName: round?.patientFirstName || '',
                    patientLastName: round?.patientLastName || '',
                    patientMRN: round?.patientMRN || '',
                    createdUserName: `${round?.userFirstName} ${round?.userLastName}`,
                    createdUserId: round?.userId || '',
                    accountNumber: round?.accountNum || '',
                    facilityId: round?.facilityId || '',
                    status:
                      round.status === 'rounded'
                        ? 'Rounded'
                        : 'Unable To Round',
                    date:
                      round?.roundDate &&
                      date.isOrbitTimestamp(round?.roundDate)
                        ? date.parseDate(round?.roundDate).toLocaleString([], {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                          })
                        : '',
                    time:
                      round?.roundDate &&
                      date.isOrbitTimestamp(round?.roundDate)
                        ? date.parseDate(round?.roundDate).toLocaleString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '',
                    roundDate: round?.roundDate || ''
                  }
                ];
              },
              []
            )
          : [];
      return roundCleanData;
    }
    return [];
  }, [patientRoundsReport, selectedUnits]);

  const handleExportCsv = useCallback(() => {
    dispatch(
      getPatientRoundsReportDownload({
        facilityId: authorizedUser?.facilityId,
        unitId: selectedUnits.map(item => item.id),
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.roundedBy || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.firstDayOfMonth
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.todayDate
        )
      })
    );
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    roundsReportFilter,
    sortKey,
    sortDir,
    selectedUnits
  ]);

  const handleClickDownloadPDF = useCallback(async () => {
    dispatch(
      getPatientRoundsReportDownload({
        facilityId: authorizedUser?.facilityId,
        unitId: selectedUnits.map(item => item.id),
        query: reportSearchQuery || '',
        roundedBy: roundsReportFilter.roundedBy || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        startday: date.formatFilterStartDate(
          roundsReportFilter.startDate || dateRange.firstDayOfMonth
        ),
        endday: date.formatFilterEndDate(
          roundsReportFilter.endDate || dateRange.todayDate
        ),
        isDownload: true
      })
    );
  }, [
    authorizedUser?.facilityId,
    reportSearchQuery,
    roundsReportFilter,
    sortKey,
    sortDir,
    selectedUnits
  ]);

  return (
    <ReportContainer id="Patient-Rounds-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <RoundsReportTable roundsReportList={listToDisplay} />
    </ReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    patientRoundsReport: state.ReportReducer.patientRoundsReport,
    reportSearchQuery: state.ReportReducer.reportSearchQuery,
    currentPatientRoundsReportPage:
      state.ReportReducer.currentPatientRoundsReportPage,
    sortKey: state.ReportReducer.sortRoundsKey,
    sortDir: state.ReportReducer.sortRoundsDir,
    roundsReportFilter: state.ReportReducer.roundsReportFilter,
    selectedUnits: state.ConfigReducer.selectedUnits,
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};

export default connect(mapReduxStateToProps)(RoundsReport);
